import type { GrayImage } from "./types";

/**
 * How strongly chroma (colourfulness) is subtracted from luminance when building
 * the whiteness map. Tuned on real photos so that brown wood grain and specular
 * glare fall below the white tiles without dragging the tiles themselves down.
 */
export const CHROMA_WEIGHT = 1.5;

/**
 * Build a single-channel "whiteness" map from RGBA pixels for *tile*
 * segmentation: high where a pixel is bright **and** close to neutral
 * (white/grey), low where it is coloured.
 *
 * Real photos place white domino tiles on a coloured surface (e.g. brown wood).
 * A plain luminance threshold (see {@link toGrayscale}) is fooled by bright wood
 * grain and specular highlights, which are nearly as light as the tiles: after
 * the morphological closing they merge with the tiles — or with each other —
 * into one blob that no longer looks tile-shaped, so tiles are missed. Measuring
 * how *white* (neutral-bright) each pixel is instead of merely how bright pushes
 * any coloured surface down while leaving the neutral tiles high:
 *
 *   whiteness = luma - CHROMA_WEIGHT * chroma
 *     luma   = Rec. 601 luminance
 *     chroma = max(r,g,b) - min(r,g,b)   → 0 for neutral, large for saturated
 *
 * For a neutral pixel (r≈g≈b) chroma is ~0, so whiteness == luma and the
 * grayscale/synthetic path is unaffected.
 */
export function toWhitenessMap(
  rgba: Uint8ClampedArray,
  width: number,
  height: number,
): GrayImage {
  const expected = width * height * 4;
  if (rgba.length !== expected) {
    throw new Error(
      `RGBA buffer length ${rgba.length} does not match ${width}x${height} (expected ${expected})`,
    );
  }

  const data = new Uint8ClampedArray(width * height);
  for (let i = 0, p = 0; i < data.length; i++, p += 4) {
    const r = rgba[p];
    const g = rgba[p + 1];
    const b = rgba[p + 2];
    const luma = 0.299 * r + 0.587 * g + 0.114 * b;
    const chroma = Math.max(r, g, b) - Math.min(r, g, b);
    // Uint8ClampedArray clamps negatives (coloured pixels) to 0.
    data[i] = Math.round(luma - CHROMA_WEIGHT * chroma);
  }

  return { width, height, data };
}
